import React from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown, FolderOpen, Plus, SearchX } from 'lucide-react';
import { Lead } from '../../../types';
import { LeadCategoryBadge } from '../../../components/LeadCategoryBadge';
import { LeadStatusBadge } from '../../../components/LeadStatusBadge';
import { formatDateToDDMMYYYY } from '../../../utils/date';
import { useHorizontalWheelScroll } from '../../../hooks/useHorizontalWheelScroll';
import { useLoadMoreOnScroll } from '../../../hooks/useLoadMoreOnScroll';
import {
  formatNetEuro,
  formatRelativeDate,
  type LeadSort,
  type LeadSortKey,
} from '../utils/leadListView';
import { LeadRowActionsMenu } from './LeadRowActionsMenu';
import { leadListAmount, projectValueTooltip } from '../utils/projectValue';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { CardListSkeleton, TableSkeletonRows } from '../../../components/TableSkeleton';

/** Zeilen pro Nachladeschritt — die Liste ist ungefiltert mehrere hundert Zeilen lang. */
const PAGE_SIZE = 60;
type SortableColumn = { key: LeadSortKey; label: string; align?: 'right' };

const COLUMNS: SortableColumn[] = [
  { key: 'title', label: 'Projekt' },
  { key: 'category', label: 'Kategorie' },
  { key: 'contact', label: 'Kontakt' },
  { key: 'address', label: 'Baustelle' },
  { key: 'amount', label: 'Betrag (netto)', align: 'right' },
  { key: 'status', label: 'Status' },
  { key: 'created', label: 'Erstellt' },
];

interface LeadListProps {
  leads: Lead[];
  isLoading?: boolean;
  sort: LeadSort;
  onSortChange: (key: LeadSortKey) => void;
  selectedIds: Set<string>;
  onToggleSelected: (id: string, selected: boolean) => void;
  onToggleAllVisible: (selected: boolean) => void;
  onSelectLead: (lead: Lead) => void;
  onDeleteLead?: (lead: Lead) => void;
  /** „In anderes Projekt überführen“ im Zeilenmenü; fehlt ohne Bearbeiten-Recht. */
  onMergeLead?: (lead: Lead) => void;
  detailHref: (lead: Lead) => string;
  deletingLeadId?: string | number | null;
  /** Unterscheidet „noch keine Projekte“ von „Filter liefert nichts“. */
  hasActiveFilters?: boolean;
  onResetFilters?: () => void;
  onCreateLead?: () => void;
}

/** Klicks aus Checkbox, Menü oder Link dürfen nicht zusätzlich die Zeile öffnen. */
const isInteractiveTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && Boolean(target.closest('a, button, input, [role="menu"]'));

export const LeadList: React.FC<LeadListProps> = ({
  leads,
  isLoading = false,
  sort,
  onSortChange,
  selectedIds,
  onToggleSelected,
  onToggleAllVisible,
  onSelectLead,
  onDeleteLead,
  onMergeLead,
  detailHref,
  deletingLeadId,
  hasActiveFilters = false,
  onResetFilters,
  onCreateLead,
}) => {
  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
  const [menuOpenLeadId, setMenuOpenLeadId] = React.useState<string | null>(null);
  const desktopScrollRef = React.useRef<HTMLDivElement>(null);
  const desktopSentinelRef = React.useRef<HTMLDivElement>(null);
  const mobileScrollRef = React.useRef<HTMLDivElement>(null);
  const mobileSentinelRef = React.useRef<HTMLDivElement>(null);
  const selectAllRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    desktopScrollRef.current?.scrollTo({ top: 0 });
    mobileScrollRef.current?.scrollTo({ top: 0 });
  }, [leads, sort.key, sort.direction]);

  const hasMore = visibleCount < leads.length;
  const loadMore = React.useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, leads.length));
  }, [leads.length]);

  useLoadMoreOnScroll(desktopScrollRef, desktopSentinelRef, hasMore, loadMore);
  useLoadMoreOnScroll(mobileScrollRef, mobileSentinelRef, hasMore, loadMore);

  // Die Tabelle ist breiter als der Viewport — Shift+Rad ersetzt das Ziehen der Leiste.
  const setDesktopScrollNode = useHorizontalWheelScroll(desktopScrollRef);

  const visibleLeads = React.useMemo(() => leads.slice(0, visibleCount), [leads, visibleCount]);

  // Checkbox-Zustand über alle gefilterten Projekte — nicht nur die geladene Slice,
  // sonst driftet er von der Sammelaktionsleiste weg (Select-all wählt sortedLeads).
  const selectedCount = leads.filter((lead) => selectedIds.has(String(lead.id))).length;
  const allSelected = leads.length > 0 && selectedCount === leads.length;

  React.useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedCount > 0 && !allSelected;
    }
  }, [selectedCount, allSelected]);

  const openLead = (lead: Lead, e: React.MouseEvent) => {
    // Modifier-Klicks dem Browser überlassen, damit „in neuem Tab“ funktioniert.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onSelectLead(lead);
  };

  const emptyState = (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      {hasActiveFilters ? (
        <>
          <SearchX className="h-8 w-8 text-slate-300" aria-hidden />
          <div>
            <p className="text-sm font-medium text-app-text">Keine Projekte gefunden</p>
            <p className="mt-1 text-sm text-app-text-subtle">
              Für diese Suche und Filter gibt es keine Treffer.
            </p>
          </div>
          {onResetFilters ? (
            <button
              type="button"
              onClick={onResetFilters}
              className="inline-flex h-9 items-center rounded-md border border-app-border bg-app-surface px-3.5 text-sm font-medium text-app-text-muted transition-colors hover:bg-app-hover"
            >
              Filter zurücksetzen
            </button>
          ) : null}
        </>
      ) : (
        <>
          <FolderOpen className="h-8 w-8 text-slate-300" aria-hidden />
          <div>
            <p className="text-sm font-medium text-app-text">Noch keine Projekte</p>
            <p className="mt-1 text-sm text-app-text-subtle">
              Lege dein erstes Projekt an, um hier den Überblick zu behalten.
            </p>
          </div>
          {onCreateLead ? (
            <button
              type="button"
              onClick={onCreateLead}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838]"
            >
              <Plus className="h-4 w-4" />
              Projekt anlegen
            </button>
          ) : null}
        </>
      )}
    </div>
  );

  return (
    <div className="flex h-full flex-col overflow-hidden bg-app-surface">
      {/* Mobile: Kartenliste */}
      <div
        ref={mobileScrollRef}
        className="app-scrollbar min-h-0 flex-1 divide-y divide-app-border overflow-y-auto overscroll-contain md:hidden"
      >
        {isLoading ? (
          <CardListSkeleton rows={8} label="Projekte werden geladen …" />
        ) : leads.length === 0 ? (
          emptyState
        ) : (
          <>
            {visibleLeads.map((lead) => (
              <a
                key={lead.id}
                href={detailHref(lead)}
                onClick={(e) => openLead(lead, e)}
                className="flex items-center gap-3 px-4 py-3 active:bg-app-surface-muted"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-app-text">
                    {lead.lead_title || 'Ohne Titel'}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-app-text-subtle">
                    {[lead.name, lead.address].filter((part) => String(part ?? '').trim()).join(' · ') || '—'}
                  </p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <LeadCategoryBadge
                      label={lead.category}
                      color={lead.category_color}
                      size="xs"
                      variant="dot"
                    />
                    <span className="text-xs text-app-text-subtle">
                      {formatRelativeDate(lead.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <LeadStatusBadge status={lead.status} />
                  <span
                    className="text-xs tabular-nums text-app-text-muted"
                    title={lead.project_value ? projectValueTooltip(lead.project_value) : undefined}
                  >
                    {formatNetEuro(leadListAmount(lead))}
                  </span>
                </div>
              </a>
            ))}
            <div ref={mobileSentinelRef} aria-hidden />
            {hasMore ? (
              <InlineLoadingState label="Weitere Projekte werden geladen …" className="px-4 py-3 text-xs" />
            ) : null}
          </>
        )}
      </div>

      {/* Desktop: Tabelle */}
      <div
        ref={setDesktopScrollNode}
        className={`app-scrollbar hidden md:flex md:min-h-0 md:flex-1 md:flex-col md:overflow-auto md:overscroll-contain ${
          // Platz für die schwebende Sammelaktionsleiste, sonst bleibt die letzte Zeile darunter.
          selectedIds.size > 0 ? 'md:pb-16' : ''
        }`}
      >
        {/*
          Feste Spaltenbreiten sind auf die längsten realen Labels ausgelegt
          („Angebot gesendet“, „Heizung / Sanitär“). Unterhalb von min-w scrollt
          die Tabelle horizontal, statt alle Spalten unlesbar zu stauchen.
        */}
        <table className="w-full min-w-[76rem] table-fixed border-separate border-spacing-0 text-left">
          <colgroup>
            <col className="w-10" />
            <col />
            <col className="w-[8rem]" />
            <col className="w-[10rem]" />
            <col className="w-[13rem]" />
            <col className="w-[7.5rem]" />
            <col className="w-[11rem]" />
            <col className="w-[7.5rem]" />
            <col className="w-[3.25rem]" />
          </colgroup>
          <thead>
            <tr>
              <th
                scope="col"
                className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5"
              >
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onToggleAllVisible(e.target.checked)}
                  disabled={leads.length === 0}
                  className="h-4 w-4 rounded border-app-border-strong accent-[#3b82f6] disabled:opacity-40"
                  aria-label="Alle Projekte auswählen"
                />
              </th>
              {COLUMNS.map((column) => (
                <SortableHeader
                  key={column.key}
                  column={column}
                  sort={sort}
                  onSortChange={onSortChange}
                />
              ))}
              <th
                scope="col"
                className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5"
              >
                <span className="sr-only">Aktionen</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <TableSkeletonRows columns={9} rows={12} label="Projekte werden geladen …" />
            ) : visibleLeads.map((lead) => {
                const id = String(lead.id);
                const isSelected = selectedIds.has(id);
                const isDeleting = deletingLeadId != null && String(deletingLeadId) === id;
                const actionsPinned = menuOpenLeadId === id;
                return (
                  <tr
                    key={lead.id}
                    onClick={(e) => {
                      if (isInteractiveTarget(e.target)) return;
                      onSelectLead(lead);
                    }}
                    className={`group h-14 cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-500/10' : 'hover:bg-app-hover'
                    } ${isDeleting ? 'opacity-50' : ''}`}
                  >
                    <td className="border-b border-app-border px-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => onToggleSelected(id, e.target.checked)}
                        className={`h-4 w-4 rounded border-app-border-strong accent-[#3b82f6] transition-opacity focus-visible:opacity-100 group-hover:opacity-100 ${
                          isSelected ? 'opacity-100' : 'opacity-0'
                        }`}
                        aria-label={`Projekt ${lead.display_id} auswählen`}
                      />
                    </td>
                    <td className="border-b border-app-border px-4">
                      <a
                        href={detailHref(lead)}
                        onClick={(e) => openLead(lead, e)}
                        className="block min-w-0 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue"
                      >
                        <span className="block truncate text-sm font-medium text-app-text group-hover:text-pipedrive-blue">
                          {lead.lead_title || 'Ohne Titel'}
                        </span>
                        <span className="mt-0.5 block truncate text-xs tabular-nums text-app-text-subtle">
                          {lead.display_id}
                        </span>
                      </a>
                    </td>
                    <td className="overflow-hidden border-b border-app-border px-4">
                      <LeadCategoryBadge
                        label={lead.category}
                        color={lead.category_color}
                        variant="dot"
                        className="max-w-full"
                      />
                    </td>
                    <td className="border-b border-app-border px-4">
                      <span
                        className="block truncate text-sm text-app-text-muted"
                        title={[lead.name, lead.email].filter(Boolean).join(' · ')}
                      >
                        {lead.name || '—'}
                      </span>
                    </td>
                    <td className="border-b border-app-border px-4">
                      <span
                        className="block min-w-0 truncate text-sm text-app-text-muted"
                        title={lead.address || undefined}
                      >
                        {lead.address?.trim() ? lead.address : <span className="text-app-text-subtle">—</span>}
                      </span>
                    </td>
                    <td className="border-b border-app-border px-4 text-right">
                      <span
                        className={`text-sm tabular-nums ${
                          leadListAmount(lead) == null ? 'text-app-text-subtle' : 'text-app-text'
                        }`}
                        title={lead.project_value ? projectValueTooltip(lead.project_value) : undefined}
                      >
                        {formatNetEuro(leadListAmount(lead))}
                      </span>
                    </td>
                    <td className="overflow-hidden border-b border-app-border px-4">
                      <LeadStatusBadge status={lead.status} />
                    </td>
                    <td className="border-b border-app-border px-4">
                      <span
                        className="whitespace-nowrap text-sm text-app-text-subtle"
                        title={formatDateToDDMMYYYY(String(lead.created_at ?? '').slice(0, 10))}
                      >
                        {formatRelativeDate(lead.created_at)}
                      </span>
                    </td>
                    <td className="border-b border-app-border px-1 text-right">
                      {onDeleteLead ? (
                        <LeadRowActionsMenu
                          detailHref={detailHref(lead)}
                          onOpen={() => onSelectLead(lead)}
                          onDelete={() => onDeleteLead(lead)}
                          onMergeInto={onMergeLead ? () => onMergeLead(lead) : undefined}
                          disabled={isDeleting}
                          onOpenChange={(open) => setMenuOpenLeadId(open ? id : null)}
                          className={`transition-opacity focus-visible:opacity-100 group-hover:opacity-100 ${
                            actionsPinned ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                      ) : null}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        {!isLoading && leads.length === 0 ? emptyState : null}
        <div ref={desktopSentinelRef} aria-hidden />
        {hasMore ? (
          <InlineLoadingState label="Weitere Projekte werden geladen …" className="py-3 text-xs" />
        ) : null}
      </div>
    </div>
  );
};

function SortableHeader({
  column,
  sort,
  onSortChange,
}: {
  column: SortableColumn;
  sort: LeadSort;
  onSortChange: (key: LeadSortKey) => void;
}) {
  const isActive = sort.key === column.key;
  const Icon = !isActive ? ChevronsUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown;

  return (
    <th
      scope="col"
      aria-sort={isActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-4 py-2.5"
    >
      <button
        type="button"
        onClick={() => onSortChange(column.key)}
        className={`group/sort inline-flex w-full items-center gap-1 text-xs font-medium transition-colors ${
          column.align === 'right' ? 'justify-end' : ''
        } ${isActive ? 'text-app-text' : 'text-app-text-subtle hover:text-app-text'}`}
      >
        <span className="truncate">{column.label}</span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 transition-opacity ${
            isActive ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-60'
          }`}
          aria-hidden
        />
      </button>
    </th>
  );
}
